(ns hai.ui.components.tooltip
  "Humble tooltip styling: Radix Themes' `Tooltip` (`c/Tooltip`) supplies the
   accessible behaviour — hover + keyboard-focus triggers, Esc dismiss, portal
   escape past clipping ancestors, collision-aware flip positioning, hoverable/
   persistent content, 200ms open delay, and reduced-motion-gated animation —
   and the rules below swap its grey-with-arrow look for the Charcoal/white
   spec.

   The rules are global rather than a class a call site opts into, so a plain
   `c/Tooltip` looks the same as one built with `tooltip` below. `.radix-themes`
   sits on the content element itself (Radix wraps the content in a `Theme`),
   so the compound selector outranks Radix Themes' own single-class rules
   whatever the stylesheet order."
  (:require
   [garden.stylesheet]
   [hai.ui.components :as c]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]))

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules tooltip-fade-keyframes
  (garden.stylesheet/at-keyframes
   :hai-tooltip-fade-in
   [:from {:opacity "0"}]
   [:to {:opacity "1"}])
  (garden.stylesheet/at-keyframes
   :hai-tooltip-fade-out
   [:from {:opacity "1"}]
   [:to {:opacity "0"}]))

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules tooltip-styles
  [".radix-themes.rt-TooltipContent"
   {:background-color t/--charcoal
    :padding "8px 10px"
    :border-radius "6px"
    :max-width "280px"
    :box-sizing "border-box"
    :animation "none"}]
  ;; Radix renders the copy in an inner <p> that carries its own colour and
  ;; size, so both have to land there rather than on the box around it.
  [".radix-themes .rt-TooltipText"
   {:color t/--colour-white
    :font-family "Manrope, sans-serif"
    :font-size "14px"
    :font-weight "500"
    :line-height "1.4"}]
  ;; Matches the box; Radix's own arrow is grey. Hiding it instead would need
  ;; `display: none !important` — Radix inlines `display: block` on it.
  [".radix-themes .rt-TooltipArrow" {:fill t/--charcoal}]
  ;; Opacity-only fade, both directions; nothing here under reduced motion, so
  ;; open/close is an instant show/hide instead.
  (garden.stylesheet/at-media
   {:prefers-reduced-motion "no-preference"}
   [".radix-themes.rt-TooltipContent[data-state='delayed-open']"
    {:animation "hai-tooltip-fade-in 0.2s ease-out"}]
   [".radix-themes.rt-TooltipContent[data-state='instant-open']"
    {:animation "hai-tooltip-fade-in 0.2s ease-out"}]
   [".radix-themes.rt-TooltipContent[data-state='closed']"
    {:animation "hai-tooltip-fade-out 0.2s ease-in"}]))

(defn tooltip
  "Wraps a single child with a tooltip above it, 8px gap, left-aligned to the
   child's left edge (flips below if there's no room above). `content` is the
   tooltip's exact copy."
  [{:keys [content]} & children]
  (into [c/Tooltip
         {:content content
          :side "top"
          :sideOffset 8
          :align "start"
          :avoidCollisions true}]
        children))
